import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAddAnnotation } from '../../api/queries';
import { ANONYMOUS, type Annotation, type AnnotationKind, type FindingDetail } from '../../api/types';
import { useSession } from '../../auth/session';
import { Field, MutationError } from '../../components/Form';
import { annotationKindMeta, formatDateTime } from '../../lib/format';

const KINDS = Object.keys(annotationKindMeta) as AnnotationKind[];

const attributionText = {
  named: 'you may be credited by name',
  anonymous: 'your contributions are shown without your name',
  hidden: 'your contributions are hidden from other members',
} as const;

function authorLabel(annotation: Annotation, selfId: string): string {
  if (annotation.authorId === selfId) return 'You';
  if (annotation.authorId === ANONYMOUS) return 'A member (name withheld)';
  return 'A member';
}

function AnnotationList({ detail, selfId }: { detail: FindingDetail; selfId: string }) {
  const annotations = [...detail.annotations].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  if (annotations.length === 0) {
    return <p className="muted">No one has added context, a correction, a challenge or a proposal yet.</p>;
  }
  return (
    <ol className="annotations">
      {annotations.map((annotation) => (
        <li key={annotation.id} className={`annotation annotation--${annotation.kind}`}>
          <p className="annotation__head">
            <span className={`annotation__kind annotation__kind--${annotation.kind}`}>{annotationKindMeta[annotation.kind].noun}</span>
            <span>{authorLabel(annotation, selfId)}</span>
            <span className="muted">
              on version {annotation.findingVersion} · {formatDateTime(annotation.createdAt)}
            </span>
          </p>
          <p className="annotation__body">{annotation.body}</p>
          {annotation.findingVersion !== detail.finding.version && (
            <p className="annotation__stale">Written about an earlier version; check whether the current version addresses it.</p>
          )}
        </li>
      ))}
    </ol>
  );
}

export function Discussion({ detail }: { detail: FindingDetail }) {
  const session = useSession();
  const mutation = useAddAnnotation(detail.finding.id);
  const [kind, setKind] = useState<AnnotationKind>('context');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<AnnotationKind | null>(null);

  function submit(event: FormEvent) {
    event.preventDefault();
    const text = body.trim();
    if (text.length < 3) {
      setError('Write at least a sentence so others and the steward can act on it.');
      return;
    }
    setError(null);
    setSent(null);
    mutation.mutate(
      { kind, body: text, expectedVersion: detail.finding.version },
      {
        onSuccess: () => {
          setBody('');
          setSent(kind);
        },
      },
    );
  }

  return (
    <div className="discussion">
      <AnnotationList detail={detail} selfId={session.user.id} />
      <form className="panel form" onSubmit={submit} aria-labelledby="contribute-title" noValidate>
        <h3 id="contribute-title" className="panel__title">
          Add to this finding
        </h3>
        <p className="panel__lede">
          You are responding to version {detail.finding.version}. A steward reads every correction, challenge and
          proposal, and records a decision with a reason.
        </p>
        <fieldset className="choice-group">
          <legend className="field__label">What are you adding?</legend>
          {KINDS.map((option) => (
            <label key={option} className={`choice${kind === option ? ' choice--selected' : ''}`}>
              <input type="radio" name="annotation-kind" value={option} checked={kind === option} onChange={() => setKind(option)} />
              <span className="choice__text">
                <span className="choice__label">{annotationKindMeta[option].label}</span>
                <span className="choice__help">{annotationKindMeta[option].help}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <Field label="Your note" error={error} help="Cite a source, date or detail where you can. Do not paste private messages.">
          {(props) => (
            <textarea
              {...props}
              className="input textarea"
              rows={5}
              maxLength={10000}
              value={body}
              onChange={(event) => setBody(event.target.value)}
            />
          )}
        </Field>
        <p className="attribution-note">
          Your attribution preference is <strong>{session.attribution ?? 'not set'}</strong>
          {session.attribution !== null && <> — {attributionText[session.attribution]}</>}. It is separate from whether you
          agree with the finding. <Link to="/participate#attribution">Change it</Link>
        </p>
        <MutationError
          error={mutation.error}
          conflictMessage="The finding changed while you were writing. Your note is kept — read the new version, then send it again."
        />
        {sent !== null && (
          <p className="form-success" role="status">
            {annotationKindMeta[sent].noun} added.
            {sent !== 'context' && ' The finding is now flagged for steward review.'}
          </p>
        )}
        <div className="button-row">
          <button type="submit" className="button button--primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Sending…' : `Add ${annotationKindMeta[kind].noun.toLowerCase()}`}
          </button>
        </div>
      </form>
    </div>
  );
}
